import React, { useRef } from "react";
import { Box, CardContent, CardMedia, Grid } from "@material-ui/core";
import Heading from "../../ui/Heading/Heading";
import EllipsedSpan from "../../ui/EllipsedSpan/EllipsedSpan";
import { LocatedParkingProduct } from "../../models/LocatedParkingProduct";
import ProductsTooltip from "./ProductsTooltip/ProductsTooltip";
import { isEmpty } from "underscore";
import locationMarker from "../../assets/images/location-marker.svg";
import seasonTicket from "../../assets/images/season-ticket.svg";
import vehicle from "../../assets/images/vehicle.svg";
import door from "../../assets/images/door.svg";

import NonBreakableSpace from "../../ui/NonBreakableSpace/NonBreakableSpace";
import {
  Inline,
  LocationCapacity,
  NoPaddingCol,
  StyledSkeleton,
  StyledCard,
  useStyles,
  GoBusinessLogo,
} from "./LocationCardStyledComponents";
import locationCardBusiness, { Position } from "./locationCard.business";
import {
  selectUseBulkVehicleAssignments,
  useGetOccupiedParkingRightsCount,
} from "../../containers/FleetManager/MyProducts/ParkingRightsDataTable/common";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../store";
import { ContractType, ParkingProductType } from "../../models/ParkingProductModel";
import { Skeleton } from "@material-ui/lab";

interface ILocationCardProps {
  model: LocatedParkingProduct;
  onClick: (position?: Position) => void;
  loading?: boolean;
}

const LocationCard: React.FC<ILocationCardProps> = ({ model, loading, onClick }) => {
  const { locationName, cityName, imageSrc, totalParkingRights, totalPmcs, parkingProducts } =
    model;
  const getTotalOccupiedParkingRights = useGetOccupiedParkingRightsCount();

  const { t } = useTranslation(["vehicleAssignment"]);
  const classes = useStyles();
  const instance = useRef<any>(null);
  const useBulkVehicleAssignments = useAppSelector(selectUseBulkVehicleAssignments);

  const onCardClicked = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
    locationCardBusiness.handleCardClicked(event, instance, (pos: Position | undefined) =>
      onClick(pos as Position)
    );

  const hasUnlimitedEntryRights = parkingProducts.some((p) => p.hasUnlimitedEntryRights);

  return (
    <StyledCard
      data-card-type="location-card"
      className={classes.root}
      ref={instance}
      onClick={onCardClicked}
    >
      <CardMedia
        className={classes.media}
        image={!imageSrc ? "undefined" : imageSrc}
        title="city image"
      >
        {model.contractType === ContractType.GoBusiness && <GoBusinessLogo/>}
      </CardMedia>
      <CardContent classes={{ root: classes.cardContentRoot }}>
        <EllipsedSpan>
          <Heading>{locationName}</Heading>
        </EllipsedSpan>
        <Grid
          container
          item
          direction="row"
          xs={12}
          classes={{ root: classes.gridRoot }}
          justifyContent="space-between"
        >
          {!isEmpty(cityName) && (
            <NoPaddingCol
              container
              item
              direction="column"
              xs={4}
              classes={{ root: classes.colRoot }}
            >
              <Inline justifyContent="flex-start">
                <embed src={locationMarker} width={16} height={16} />
                <NonBreakableSpace />
                <EllipsedSpan>{cityName}</EllipsedSpan>
              </Inline>
            </NoPaddingCol>
          )}
          <Grid item xs={2} justifyContent="center" direction="row" container>
            {loading ? (
              <Skeleton width="100%" />
            ) : (
              <Box className={classes.colRoot}>
                <embed src={seasonTicket} width={16} height={16} />
                <NonBreakableSpace />
                <span>{totalPmcs}</span>
              </Box>
            )}
          </Grid>
          <NoPaddingCol
            container
            item
            direction="column"
            xs={6}
            classes={{ root: classes.colRoot }}
          >
            {loading ? (
              <StyledSkeleton />
            ) : (
              <Grid container justifyContent="flex-start">
                <ProductsTooltip
                  parkingProducts={parkingProducts.filter(
                    (x) => x.productType !== ParkingProductType.PedestrianAccess
                  )}
                  displayAmount={true}
                >
                  <LocationCapacity>
                    <embed src={vehicle} width={16} height={16} />
                    <NonBreakableSpace />
                    {getTotalOccupiedParkingRights(model)} /{" "}
                    {useBulkVehicleAssignments && hasUnlimitedEntryRights
                      ? t("unlimited")
                      : totalParkingRights}
                  </LocationCapacity>
                </ProductsTooltip>
                {model.totalPedestrianRights > 0  && (
                  <ProductsTooltip
                    parkingProducts={parkingProducts.filter(
                      (x) => x.productType === ParkingProductType.PedestrianAccess
                    )}
                    displayAmount={true}
                  >
                    <LocationCapacity>
                      <embed src={door} width={16} height={16} />
                      <NonBreakableSpace />
                      {model.totalDelegatedPedestrianRights}/{model.totalPedestrianRights}                    
                    </LocationCapacity>
                  </ProductsTooltip>
                )}
              </Grid>
            )}
          </NoPaddingCol>
        </Grid>
      </CardContent>
    </StyledCard>
  );
};

LocationCard.defaultProps = {
  model: {
    locationName: "Location name",
    cityName: "City name",
    placeId: 0,
    imageSrc: "",
    totalDelegatedParkingRights: 0,
    totalParkingRights: 0,
    totalAssignedParkingRights: 0,
    totalPmcs: 0,
    parkingProducts: [],
    hasFlexBudgedSeasonTickets: false,
    totalDelegatedPedestrianRights: 0,
    totalPedestrianRights: 0,
    contractType: ContractType.SeasonTicket,
  },
  onClick: () => {},
} satisfies ILocationCardProps;

export default LocationCard;
